import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Phone, MapPin, Clock, CheckCircle, User, 
  Truck, X, ExternalLink, PhoneCall, Shield, Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';

const SOSAlertPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');

  useEffect(() => {
    fetchAlerts();
    // Poll for new alerts every 10 seconds
    const interval = setInterval(fetchAlerts, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/sos/alerts/active');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToAlert = async (alertId) => {
    try {
      await api.put(`/sos/alerts/${alertId}/respond`);
      toast.success('Marked as responding');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const markCalled911 = async (alertId) => {
    try {
      await api.put(`/sos/alerts/${alertId}/called-911`);
      toast.success('Marked 911 as called');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const resolveAlert = async (alertId, status = 'resolved') => {
    try {
      await api.put(`/sos/alerts/${alertId}/resolve`, {
        status,
        notes: resolveNotes,
      });
      toast.success(`Alert marked as ${status}`);
      setShowResolveDialog(false);
      setResolveNotes('');
      setSelectedAlert(null);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to resolve');
    }
  };

  const openInMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const callDriver = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      toast.error('No phone number available');
    }
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-red-600" />
          <p className="text-red-600 mt-2">Loading alerts...</p>
        </CardContent>
      </Card>
    );
  }

  if (alerts.length === 0) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-green-700">
            <Shield className="w-5 h-5" />
            SOS Alert Monitor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 text-green-600">
            <CheckCircle className="w-6 h-6" />
            <span>No active emergencies. All drivers are safe.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-red-300 bg-red-50 animate-pulse-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 animate-pulse" />
            🚨 ACTIVE SOS ALERTS ({alerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {alerts.map((alert) => (
            <div 
              key={alert.id}
              className={`p-4 rounded-lg border-2 ${
                alert.status === 'active' 
                  ? 'bg-red-100 border-red-400' 
                  : 'bg-yellow-100 border-yellow-400'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    alert.status === 'active' ? 'bg-red-600' : 'bg-yellow-500'
                  }`}>
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 flex items-center gap-2">
                      {alert.driver_name}
                      {alert.status === 'responding' && (
                        <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                          RESPONDING
                        </span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600">{alert.company_name || 'Independent Driver'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    alert.status === 'active' ? 'bg-red-600 text-white' : 'bg-yellow-500 text-white'
                  }`}>
                    {alert.status.toUpperCase()}
                  </span>
                  <p className="text-xs text-gray-500 mt-1 flex items-center justify-end gap-1">
                    <Clock className="w-3 h-3" />
                    {getTimeSince(alert.created_at)}
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start gap-2 mb-3 p-2 bg-white rounded">
                <MapPin className="w-4 h-4 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {alert.location_address || `${alert.latitude.toFixed(4)}, ${alert.longitude.toFixed(4)}`}
                  </p>
                  <button
                    onClick={() => openInMaps(alert.latitude, alert.longitude)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open in Google Maps
                  </button>
                </div>
              </div>

              {/* Load Info */}
              {alert.load_info && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-white rounded">
                  <Truck className="w-4 h-4 text-gray-600" />
                  <div>
                    <p className="text-sm text-gray-900">{alert.load_info}</p>
                    {alert.vehicle_info && (
                      <p className="text-xs text-gray-500">Hauling: {alert.vehicle_info}</p>
                    )}
                  </div>
                </div>
              )}

              {/* 911 Status */}
              {alert.called_911 && (
                <div className="flex items-center gap-2 mb-3 p-2 bg-blue-100 rounded">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">
                    911 Called at {new Date(alert.called_911_at).toLocaleTimeString()}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                {alert.driver_phone && (
                  <Button
                    size="sm"
                    onClick={() => callDriver(alert.driver_phone)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <PhoneCall className="w-4 h-4 mr-1" />
                    Call Driver
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => window.location.href = 'tel:911'}
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Call 911
                </Button>

                {!alert.called_911 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markCalled911(alert.id)}
                    className="border-blue-300 text-blue-600"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Mark 911 Called
                  </Button>
                )}

                {alert.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => respondToAlert(alert.id)}
                    className="border-yellow-400 text-yellow-700"
                  >
                    Responding
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedAlert(alert);
                    setShowResolveDialog(true);
                  }}
                  className="border-gray-300"
                >
                  <X className="w-4 h-4 mr-1" />
                  Resolve
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Resolve SOS Alert</DialogTitle>
            <DialogDescription>
              Mark this alert as resolved or a false alarm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Resolution Notes</label>
              <textarea
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                placeholder="Add any notes about the resolution..."
                className="w-full mt-1 p-2 border border-gray-300 rounded-lg text-sm"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => resolveAlert(selectedAlert?.id, 'resolved')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolved - Driver Safe
              </Button>
              <Button
                onClick={() => resolveAlert(selectedAlert?.id, 'false_alarm')}
                variant="outline"
                className="flex-1"
              >
                False Alarm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes pulse-border {
          0%, 100% { border-color: rgb(252, 165, 165); }
          50% { border-color: rgb(239, 68, 68); }
        }
        .animate-pulse-border {
          animation: pulse-border 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default SOSAlertPanel;
